import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { Bold, Italic, Heading2, Heading3, List, ListOrdered, Quote, Link2, ImagePlus, Undo2, Redo2, Minus } from "lucide-react";
import { toast } from "sonner";
import { uploadImage } from "@/lib/upload";

function Tool({ on, active, children, label }) {
  return (
    <button type="button" onClick={on} title={label} aria-label={label} data-testid={`rte-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`p-2 rounded-md transition-colors ${active ? "bg-[#1E3A2B] text-white" : "hover:bg-[#F0E8DE]"}`}>{children}</button>
  );
}

export function RichTextEditor({ value, onChange }) {
  const editor = useEditor({
    extensions: [StarterKit.configure({ heading: { levels: [2, 3] } }), Link.configure({ openOnClick: false }), Image],
    content: value,
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
    editorProps: { attributes: { class: "jt-prose min-h-[320px] p-4 focus:outline-none", "data-testid": "rte-content" } },
  });
  if (!editor) return null;
  const c = () => editor.chain().focus();
  const link = () => {
    const url = window.prompt("Link URL", editor.getAttributes("link").href || "https://");
    if (url === null) return;
    if (!url) c().unsetLink().run(); else c().extendMarkRange("link").setLink({ href: url }).run();
  };
  const image = () => {
    const input = document.createElement("input");
    input.type = "file"; input.accept = "image/*";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const url = await uploadImage(file);
        const alt = window.prompt("Describe the image (alt text, good for SEO)", "") || "";
        c().setImage({ src: url, alt }).run();
      } catch (e) { toast.error(e.message); }
    };
    input.click();
  };

  return (
    <div className="rounded-xl border border-[#E2DACF] bg-white" data-testid="rte">
      <div className="flex flex-wrap gap-1 border-b border-[#E2DACF] p-1.5 sticky top-0 bg-white z-10 rounded-t-xl">
        <Tool label="Bold" on={() => c().toggleBold().run()} active={editor.isActive("bold")}><Bold size={15}/></Tool>
        <Tool label="Italic" on={() => c().toggleItalic().run()} active={editor.isActive("italic")}><Italic size={15}/></Tool>
        <Tool label="Heading" on={() => c().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })}><Heading2 size={15}/></Tool>
        <Tool label="Subheading" on={() => c().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })}><Heading3 size={15}/></Tool>
        <Tool label="Bullet list" on={() => c().toggleBulletList().run()} active={editor.isActive("bulletList")}><List size={15}/></Tool>
        <Tool label="Numbered list" on={() => c().toggleOrderedList().run()} active={editor.isActive("orderedList")}><ListOrdered size={15}/></Tool>
        <Tool label="Quote" on={() => c().toggleBlockquote().run()} active={editor.isActive("blockquote")}><Quote size={15}/></Tool>
        <Tool label="Divider" on={() => c().setHorizontalRule().run()}><Minus size={15}/></Tool>
        <Tool label="Link" on={link} active={editor.isActive("link")}><Link2 size={15}/></Tool>
        <Tool label="Image" on={image}><ImagePlus size={15}/></Tool>
        <span className="flex-1"/>
        <Tool label="Undo" on={() => c().undo().run()}><Undo2 size={15}/></Tool>
        <Tool label="Redo" on={() => c().redo().run()}><Redo2 size={15}/></Tool>
      </div>
      <EditorContent editor={editor}/>
    </div>
  );
}
